import { useEffect, useState } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import chabadLogo from '../../assets/reshet.png';
import FmdGoodIcon from '@mui/icons-material/FmdGood';
import BadgeIcon from '@mui/icons-material/Badge';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { object, string } from 'yup';
import { FormProvider } from '@sm-dashboard/form-provider';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import { InputAdornment } from '@mui/material';
import AuthLayout from '../../layouts/AuthLayout';
import Copyright from '../../components/auth/Copyright';
import { useFetch } from '../../hooks/useFetch';
import { reportUrl } from '../../constants';
import { useAppContext } from '@/context/context';

type FormValuesProps = {
  name: string;
  institution: string;
  report: string;
};

const schema = object({
  name: string().required('שם זהו שדה חובה'),
  institution: string().required('סניף זהו שדה חובה'),
  report: string().required('הערה זהו שדה חובה')
}).required();

export const ReportForm = ({ setIsReported }: { setIsReported?: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const { setSnackbar } = useAppContext();
  const { control } = useFormContext() || {};
  const { data, error, loading, fetchData } = useFetch();

  const methods = useForm<FormValuesProps, Event>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      institution: '',
      report: ''
    }
  });

  const { handleSubmit } = methods;
  const onSubmit = (data: FormValuesProps) => {
    fetchData({ method: 'POST', url: reportUrl, data });
  };

  useEffect(() => {
    if (data) {
      setIsReported && setIsReported(true);
      setSnackbar({ severity: 'success', open: true, message: 'פנייה נשלחה בהצלחה' });
    }
  }, [data]);

  return (
    <FormProvider onSubmit={handleSubmit(onSubmit)} methods={methods}>
      <Box sx={{ mt: 1 }}>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size="small"
              label="שם"
              type="text"
              error={!!error}
              variant="outlined"
              helperText={error?.message}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <BadgeIcon color="primary" />
                  </InputAdornment>
                )
              }}
            />
          )}
          rules={{ required: true }}
        />
        <Controller
          name="institution"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              size="small"
              label="סניף"
              type="text"
              sx={{ mt: 2 }}
              error={!!error}
              variant="outlined"
              helperText={error?.message}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <FmdGoodIcon color="primary" />
                  </InputAdornment>
                )
              }}
            />
          )}
          rules={{ required: true }}
        />
        <Controller
          name="report"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              sx={{ mt: 2 }}
              label="הערה"
              error={!!error}
              helperText={error?.message}
              multiline
              rows={4}
              type="text"
              fullWidth
            />
          )}
          rules={{ required: true }}
        />

        <LoadingButton loading={loading} type="submit" fullWidth variant="contained" sx={{ mt: 3 }}>
          דווח{' '}
        </LoadingButton>
      </Box>
    </FormProvider>
  );
};

const Report = () => {
  const navigate = useNavigate();
  const [isReported, setIsReported] = useState(false);

  return (
    <AuthLayout>
      {isReported ? (
        <Container
          sx={{
            width: {
              sm: '90%',
              md: '90%',
              lg: '90%',
              xl: '90%'
            },
            height: '100px',
            backgroundColor: 'common.white',
            borderRadius: '15px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '4',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          component="main"
          maxWidth="xs"
        >
          <Typography
            sx={{ marginTop: 1, textAlign: 'center' }}
            component="h1"
            variant="subtitle1"
            color="text.primary"
          >
            פנייתך התקבלה! נחזור אליך בהקדם.
            <br />
            לכניסה למערכת {''}
            <Link
              color="text.disabled"
              sx={{ cursor: 'pointer' }}
              onClick={() => {
                navigate('/login');
              }}
            >
              לחץ כאן
            </Link>
          </Typography>
        </Container>
      ) : (
        <Container
          sx={{
            width: {
              sm: '90%',
              md: '90%',
              lg: '90%',
              xl: '90%'
            },
            backgroundColor: 'common.white',
            borderRadius: '15px',
            boxShadow: '4',
            alignItems: 'center'
          }}
          component="main"
          maxWidth="xs"
        >
          <CssBaseline />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            <Box
              component="img"
              sx={{
                width: '60px',
                marginTop: 4
              }}
              alt="chabad logo"
              src={chabadLogo}
            />
            <Typography sx={{ marginTop: 1 }} component="h1" variant="subtitle1" color="text.primary">
              דווח לנו במקרה שאינך נמצא במערכת{' '}
            </Typography>
            <ReportForm setIsReported={setIsReported} />
          </Box>

          <Copyright height={50} sx={{ mt: 3, mb: 6 }} />
        </Container>
      )}
    </AuthLayout>
  );
};

export default Report;
